import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { Password } from 'primereact/password'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useId, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { useCheckResetTokenQuery, useResetPasswordMutation } from '~/hooks/authApi'
import useToast from '~/hooks/toast'
import type { ErrorObj } from '~/utils/httpClient'

const resetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'password must be minimum of 8 characters')
      .regex(/[a-z]/, 'password must contain at least 1 lowercase letter')
      .regex(/[A-Z]/, 'password must contain at least 1 uppercase letter')
      .regex(/[0-9]/, 'password must contain at least 1 number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

const PasswordResetTimer = ({
  userCanSubmitUntil,
  onExpire,
}: {
  userCanSubmitUntil: dayjs.Dayjs
  onExpire: () => void
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    const diff = userCanSubmitUntil.diff(dayjs(), 'second')
    return Math.max(0, diff)
  })

  useEffect(() => {
    if (!userCanSubmitUntil.isValid()) return

    const updateTimer = () => {
      const now = dayjs()
      const diff = userCanSubmitUntil.diff(now, 'second')
      const newTimeLeft = Math.max(0, diff)
      setTimeLeft(newTimeLeft)

      if (newTimeLeft <= 0) {
        onExpire()
      }
    }

    // Initial check
    updateTimer()

    const interval = setInterval(updateTimer, 1000)
    return () => clearInterval(interval)
  }, [userCanSubmitUntil, onExpire])

  if (timeLeft <= 0) return null

  const hours = Math.floor(timeLeft / 3600)
  const minutes = Math.floor((timeLeft % 3600) / 60)
  const seconds = timeLeft % 60

  const formatTime = () => {
    const mm = String(minutes).padStart(2, '0')
    const ss = String(seconds).padStart(2, '0')

    if (hours > 0) {
      const hh = String(hours).padStart(2, '0')
      return `${hh}:${mm}:${ss}`
    }
    return `${minutes}:${ss}`
  }

  return (
    <div className='text-center mb-4'>
      <span className={`text-sm font-semibold ${timeLeft < 60 ? 'text-red-500' : 'text-gray-500'}`}>
        Time remaining: {formatTime()}
      </span>
    </div>
  )
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const toast = useToast()
  const passwordId = useId()
  const confirmPasswordId = useId()

  const { data, isLoading: isChecking, isError } = useCheckResetTokenQuery(token)
  const expiryTsStr = data?.expiryTs
  const expiryTs = useMemo(() => dayjs(expiryTsStr), [expiryTsStr])
  const userCanSubmitUntil = useMemo(() => expiryTs.subtract(1, 'minute'), [expiryTs])

  const [isExpired, setIsExpired] = useState(false)

  useEffect(() => {
    if (userCanSubmitUntil.isValid()) {
      setIsExpired(dayjs().isAfter(userCanSubmitUntil))
    }
  }, [userCanSubmitUntil])

  const isValid = !!data && expiryTs.isValid() && !isExpired

  const resetMutation = useResetPasswordMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<z.infer<typeof resetPasswordSchema>>({
    resolver: zodResolver(resetPasswordSchema),
  })

  const onSubmit = (data: z.infer<typeof resetPasswordSchema>) => {
    resetMutation.mutate(
      {
        token,
        password: data.password,
      },
      {
        onSuccess: () => {
          toast?.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Your password has been set successfully.',
            life: 3000,
          })
          navigate('/auth/login')
        },
        onError: (oe: unknown) => {
          const e = oe as ErrorObj
          toast?.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: e.message || 'Failed to set password',
          })
        },
      },
    )
  }

  if (isChecking) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <ProgressSpinner />
      </div>
    )
  }

  const tokenInvalid = !token || isError || !isValid

  return (
    <div className='flex flex-col justify-center items-center min-h-screen w-full bg-teal-200'>
      <div className='w-60 md:w-80 p-4 py-6 rounded-md shadow-md bg-white'>
        <h1 className='font-bold text-center text-2xl mb-6'>Set Your Password</h1>

        {tokenInvalid ? (
          <div className='text-center'>
            <Message severity='error' text='This link is invalid or has expired.' />
            <div className='mt-4'>
              <Button label='Request New Link' onClick={() => navigate('/auth/forgot-password')} />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className='p-fluid'>
            <PasswordResetTimer
              userCanSubmitUntil={userCanSubmitUntil}
              onExpire={() => setIsExpired(true)}
            />
            <div className='field my-4'>
              <span className='p-float-label'>
                <Password
                  id={passwordId}
                  {...register('password')}
                  onChange={(e) => setValue('password', e.target.value)}
                  toggleMask
                  className={errors.password ? 'p-invalid' : ''}
                />
                <label htmlFor={passwordId}>New Password</label>
              </span>
              {errors.password && (
                <Message severity='error' text={errors.password.message} className='mt-2' />
              )}
            </div>

            <div className='field my-4'>
              <span className='p-float-label'>
                <Password
                  id={confirmPasswordId}
                  {...register('confirmPassword')}
                  onChange={(e) => setValue('confirmPassword', e.target.value)}
                  toggleMask
                  feedback={false}
                  className={errors.confirmPassword ? 'p-invalid' : ''}
                />
                <label htmlFor={confirmPasswordId}>Confirm Password</label>
              </span>
              {errors.confirmPassword && (
                <Message severity='error' text={errors.confirmPassword.message} className='mt-2' />
              )}
            </div>

            <Button
              type='submit'
              label='Set Password'
              className='mt-4'
              loading={resetMutation.isLoading}
            />
          </form>
        )}
      </div>
    </div>
  )
}

export default ResetPasswordPage
